import { Component, computed, inject, signal } from '@angular/core';
import { FormControl, ReactiveFormsModule } from '@angular/forms';
import { RouterLink } from '@angular/router';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';
import { KnowledgeBaseService } from '../../../../core/services/knowledge-base.service';
import { KnowledgeArticleSummary, KnowledgeCategory } from '../../../../core/models';
import { resourceTypeLabel, resourceTypeLabelKey } from '../../../../shared/utils/knowledge-article.mapper';
import { ScrollReveal } from '../../../../shared/directives/scroll-reveal';

/**
 * UC-24 : centre de documentation public (support-service, /support/public/knowledge-base et
 * /support/public/categories). `resourceType` (Article/Guide/Support de formation/…) n'a pas de
 * paramètre serveur combinable avec `query`/`categoryCode` sur ce endpoint (voir
 * KnowledgeBaseService) : filtré côté client, à partir des valeurs réellement présentes dans les
 * résultats déjà chargés.
 */
@Component({
  selector: 'app-documentation',
  imports: [RouterLink, ReactiveFormsModule, ScrollReveal, TranslatePipe],
  templateUrl: './documentation.html',
})
export class Documentation {
  private readonly knowledgeBase = inject(KnowledgeBaseService);
  private readonly translate = inject(TranslateService);

  /** Libellé traduit d'un type de ressource (repli sur le libellé brut si le type est inconnu). */
  resourceTypeLabel(type: string): string {
    const key = resourceTypeLabelKey(type);
    return key ? this.translate.instant(key) : resourceTypeLabel(type);
  }

  readonly queryControl = new FormControl('', { nonNullable: true });
  readonly categoryFilter = signal('');
  readonly resourceTypeFilter = signal('');

  readonly categories = signal<KnowledgeCategory[]>([]);
  readonly articles = signal<KnowledgeArticleSummary[]>([]);
  readonly loading = signal(true);
  readonly error = signal(false);

  readonly resourceTypeOptions = computed(() => {
    const types = new Set(this.articles().map((a) => a.resourceType));
    return [...types].sort();
  });

  readonly filteredArticles = computed(() => {
    const type = this.resourceTypeFilter();
    const all = this.articles();
    return type ? all.filter((a) => a.resourceType === type) : all;
  });

  constructor() {
    this.knowledgeBase.publicCategories().subscribe({
      next: (categories) => this.categories.set(categories),
      error: () => undefined,
    });
    this.search();
  }

  onSubmit(event: Event): void {
    event.preventDefault();
    this.search();
  }

  onCategoryChange(event: Event): void {
    this.categoryFilter.set((event.target as HTMLSelectElement).value);
    this.search();
  }

  setResourceTypeFilter(type: string): void {
    this.resourceTypeFilter.set(type);
  }

  private search(): void {
    this.loading.set(true);
    this.error.set(false);
    this.knowledgeBase
      .publicArticles({ query: this.queryControl.value.trim() || undefined, categoryCode: this.categoryFilter() || undefined })
      .subscribe({
        next: (page) => {
          this.articles.set(page.content);
          this.resourceTypeFilter.set('');
          this.loading.set(false);
        },
        error: () => {
          this.loading.set(false);
          this.error.set(true);
        },
      });
  }
}
